import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { NullableString } from "../../../../../../core/common_types";
import { useStoreDispatch, useStoreSelector } from "../../../../../../../hooks/StoreHooks";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useDialogs } from "../../../../../../providers/dialogs_provider";
import { useFormik } from "formik";
import { useFinishes } from "../../../../../../providers/finish_provider";
import { usePergolaConfigurationBuilderActions } from "../../../../../../../store/reducers/pergola_configuration_builder";
import { AspectPergola } from "../../../../../../models/pergola_configuration";

export type FormColorType = {
  coloreTelaio: NullableString;
  coloreLamelle: NullableString;
};

export const FormColor: FC<{ colors: any[]; model: FormColorType; setModel: React.Dispatch<React.SetStateAction<FormColorType>> }> = ({ colors, model, setModel }) => {
  const { pergolaConfigurationBuilder } = useStoreSelector((store) => store);
  const {setPergolaAspect} = usePergolaConfigurationBuilderActions();
  const dialogs = useDialogs();
  const dispatcher = useStoreDispatch();
  const standardColorCodes = ["9010","7016","10000","10003","10007","10008","10009","10004","10005"];
  const finishesProvider = useFinishes();

  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [isCustomTelaio, setIsCustomTelaio] = useState(false);
  const [isCustomLamelle, setIsCustomLamelle] = useState(false);

  const formik = useFormik({
    initialValues: {
      coloreTelaio: pergolaConfigurationBuilder.configuration?.aspetto.colore_telaio,
      coloreLamelle: pergolaConfigurationBuilder.configuration?.aspetto.colore_lamelle
    },
    onSubmit: () => {}
  });
  useEffect(() => {
    if (model.coloreTelaio && standardColorCodes.includes(model.coloreTelaio)) {
      setIsCustomTelaio(false);
    } else {
      setIsCustomTelaio(true);
    }
    if (model.coloreLamelle && standardColorCodes.includes(model.coloreLamelle)) {
      setIsCustomLamelle(false);
    } else {
      setIsCustomLamelle(true);
    }
  }, [model.coloreTelaio]);
  useEffect(() => {
    const loadPreviews = async () => {
      const entries = await Promise.all(
        standardColorCodes.map(async (code) => {
          const img = await finishesProvider?.getImgPreview(code);
          return [code, img?.src ?? ""] as [string, string];
        })
      );
      setPreviews(Object.fromEntries(entries));
    };
    
    loadPreviews();
  }, [finishesProvider]);

  const renderStandardColorMenuItems = () =>
    standardColorCodes.map(c => (
      <MenuItem key={c} value={c}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          {finishesProvider?.getCodeName(c)}
          {previews[c] && (
            <img src={previews[c]} alt={c} style={{ width: "2rem", height: "1.75rem", marginLeft: "1rem" }} />
          )}
        </div>
      </MenuItem>
    ));

  return (
    <div>
      <h5 className="text-primary">Colori</h5>

      {/* Telaio */}
      <FormControl fullWidth className="mt-3">
        <InputLabel id="colore_telaio">Colore Telaio</InputLabel>
        <Select
          labelId="colore_telaio"
          value={isCustomTelaio ? "ral_non_standard" : model.coloreTelaio || ""}
          label="Colore Telaio"
          onChange={(e) => {
            const selected = e.target.value;
            if (selected === "ral_non_standard") {
              setIsCustomTelaio(true);
              setModel({ ...model, coloreTelaio: selected });
              formik.setFieldValue("coloreTelaio", "");
            } else {
              setIsCustomTelaio(false);
              setModel({ ...model, coloreTelaio: selected });
              const value: AspectPergola = {colore_telaio: selected,colore_lamelle: model.coloreLamelle}
              dispatcher(setPergolaAspect(value));
            }
          }}
        >
          {renderStandardColorMenuItems()}
          <MenuItem value="ral_non_standard">Colore personalizzato</MenuItem>
        </Select>
      </FormControl>

      {isCustomTelaio && (
        <TextField
          fullWidth
          className="mt-2"
          label={!formik.values.coloreTelaio ? "Seleziona il colore" : ""}
          name="coloreTelaio"
          value={finishesProvider?.getRalName(formik.values.coloreTelaio)}
          InputProps={{
            readOnly: true,
            startAdornment: formik.values.coloreTelaio && (
              <InputAdornment position="start">
                {finishesProvider?.getRalPreview(formik.values.coloreTelaio)}
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() =>
                    dialogs?.selectRalColor({
                      skipRals: standardColorCodes,
                      onConfirm: (selectedColor) => {
                        formik.setFieldValue("coloreTelaio", selectedColor);
                        setModel({ ...model, coloreTelaio: selectedColor });
                        dispatcher({ type: "setColoreTelaio", payload: selectedColor });
                        const value: AspectPergola = {colore_telaio: selectedColor,colore_lamelle: formik.values.coloreLamelle}
                        dispatcher(setPergolaAspect(value));
                      }
                    })
                  }
                >
                  <OpenInNewIcon style={{ color: "black" }} />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      )}

      {/* Lamelle */}
      <FormControl fullWidth className="mt-3">
        <InputLabel id="colore_lamelle">Colore Lamelle</InputLabel>
        <Select
          labelId="colore_lamelle"
          value={isCustomLamelle ? "ral_non_standard" : model.coloreLamelle || ""}
          label="Colore Lamelle"
          onChange={(e) => {
            const selected = e.target.value;
            if (selected === "ral_non_standard") {
              setIsCustomLamelle(true);
              setModel({ ...model, coloreLamelle: selected });
              formik.setFieldValue("coloreLamelle", "");
            } else {
              setIsCustomLamelle(false);
              setModel({ ...model, coloreLamelle: selected });
              const value: AspectPergola = {colore_telaio: model.coloreTelaio,colore_lamelle: selected}
              dispatcher(setPergolaAspect(value));
            }
          }}
        >
          {renderStandardColorMenuItems()}
          <MenuItem value="ral_non_standard">Colore personalizzato</MenuItem>
        </Select>
      </FormControl>

      {isCustomLamelle && (
        <TextField
          fullWidth
          className="mt-2"
          label={!formik.values.coloreLamelle ? "Seleziona il colore" : ""}
          name="coloreLamelleNsRal"
          value={finishesProvider?.getRalName(formik.values.coloreLamelle)}
          InputProps={{
            readOnly: true,
            startAdornment: formik.values.coloreLamelle && (
              <InputAdornment position="start">
                {finishesProvider?.getRalPreview(formik.values.coloreLamelle)}
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() =>
                    dialogs?.selectRalColor({
                      skipRals: standardColorCodes,
                      onConfirm: (selectedColor) => {
                        formik.setFieldValue("coloreLamelle", selectedColor);
                        setModel({ ...model, coloreLamelle: selectedColor });
                        dispatcher({ type: "setColoreLamelle", payload: selectedColor });
                        const value: AspectPergola = {colore_telaio: model.coloreTelaio,colore_lamelle: selectedColor}
                        dispatcher(setPergolaAspect(value));
                      }
                    })
                  }
                >
                  <OpenInNewIcon style={{ color: "black" }} />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      )}
    </div>
  );
};
